import { action, isObservableArray, makeObservable, observable } from 'mobx'
import moment from 'moment'
import * as R from 'ramda'
import { cloneDeep } from 'lodash'
import i18n from 'i18next'
import { v4 as uuidv4 } from 'uuid'

import { getProductMap, getProductTypes, getSurveyTypes, getRespondentTypes } from '../../../../utils/helpers'

const isBlank = (value) => R.isNil(value) || R.isEmpty(value)

class customTagStore {
    @observable isLoading = true

    formFields = {}

    clients = []

    @observable tagList = []

    @observable searchData = []

    @observable groups = []

    productMap = {}

    @observable industries = []

    @observable productTypes = []

    @observable respondentTypes = []

    @observable surveyTypes = []

    divisions = []

    @observable portfolios = []

    @observable showBenchmarksModal = false

    @observable groupModalOpen = false

    groupFormFields = {}

    @observable groupForm = {
        editingGroupId: undefined,
        name: '',
        cascade: false,
        tagIdsByOid: {},
    }

    @observable currentTagGroup = undefined

    @observable benchamrkOptionIndex = undefined

    @observable isCustomBenchmark = false

    @observable customTag = {
        name: undefined,
        description: undefined,
        clientId: undefined,
        oid: undefined,
        industry: undefined,
        productType: undefined,
        respondentType: undefined,
        surveyType: undefined,
        divisionId: [],
        portfolioId: [],
        questionType: undefined,
        uiQuestionType: undefined,
        options: [],
        defaultValue: false,
        config: {
            isMandatory: false,
            answerOptions: [],
            mergefields: undefined,
            dateOptions: [],
            benchmarkOptions: [],
            benchmarkRules: [],
        },
    }

    @observable testBenchmark = undefined

    constructor(store, isCustomBenchmark = false) {
        makeObservable(this)
        this.store = store
        this.isCustomBenchmark = isCustomBenchmark
    }

    register = (name, validate) => {
        const { formFields } = this
        formFields[name] = validate
    }

    unregister = (name) => {
        const { formFields } = this
        Reflect.deleteProperty(formFields, name)
    }

    @action
    onBlur = async ({ name, value, type }, promise, fieldReset, path) => {
        let fieldValue = value

        if (type === 'checkboxgroup' || type === 'benchmarkOptions') {
            fieldValue = value.slice()
        } else if (Array.isArray(value) || isObservableArray(value)) {
            fieldValue = value.slice().map(({ value }) => value)
        }

        if (fieldReset) fieldReset(this.customTag, fieldValue)

        if (promise) await promise(fieldValue, this.customTag)

        const lens = R.lensPath(path)
        this.customTag = R.set(lens, fieldValue, this.customTag)
    }

    getLevels = () => (
        [
            {
                value: 3,
                label: 'Client',
            },
            {
                value: 4,
                label: 'Division',
            },
            {
                value: 5,
                label: 'Portfolio',
            },
            {
                value: 6,
                label: 'Event',
            },
            {
                value: 7,
                label: 'Survey',
            },
            {
                value: 9,
                label: 'Benchmark',
                isHidden: !this.isCustomBenchmark,
            },
            {
                value: 10,
                label: 'Integration',
            },
            {
                value: 11,
                label: 'User',
            },
        ].filter(({ isHidden }) => (
            !isHidden
        ))
    )

    getOptions = () => (
        [
            {
                value: 'mergefield',
                label: i18n.t('customTag.edit.option.mergefield'),
                isHidden: !(
                    ['SB', 'CH', 'TX', 'I'].includes(this.customTag.questionType)
                    && [6, 7, 10].includes(this.customTag.oid)
                ),
            },
            {
                value: 'benchmark',
                label: i18n.t('customTag.edit.option.benchmark'),
                isHidden: !(
                    ['SB', 'CH'].includes(this.customTag.questionType)
                    && ![7, 10].includes(this.customTag.oid)
                    && this.isCustomBenchmark
                ),
            },
            {
                value: 'qlist',
                label: i18n.t('customTag.edit.option.qlist'),
                isHidden: !(
                    ['SB', 'CH'].includes(this.customTag.questionType)
                    && [4, 5, 6].includes(this.customTag.oid)
                ),
            },
            {
                value: 'registration',
                label: i18n.t('customTag.edit.option.registration'),
                isHidden: !(
                    this.customTag.questionType === 'TX'
                    && this.customTag.oid === 7
                ),
            },
            {
                value: 'template',
                label: i18n.t('customTag.edit.option.template'),
                isHidden: !(
                    ['SB', 'CH'].includes(this.customTag.questionType)
                    && [3, 4, 5, 6, 7].includes(this.customTag.oid)
                ),
            },
        ].filter(({ isHidden }) => (
            !isHidden
        ))
    )

    getQuestionTypes = () => (
        [
            {
                value: 'SB',
                label: 'Drop Down',
            },
            {
                value: 'CH',
                label: 'Multi-Choice',
            },
            {
                value: 'CH-AT', // Multi-choice locked with a single admin tag (one option only)
                label: 'Admin Tag',
            },
            {
                value: 'TX',
                label: 'Text',
            },
            {
                value: 'I',
                label: 'Numeric',
            },
            {
                value: 'DT',
                label: 'Date',
                isHidden: this.customTag.oid !== 7,
            },
        ].filter(({ isHidden }) => !isHidden)
    )

    getFormFields = () => {
        const { customTag } = this
        const fields = [
            {
                name: 'name',
                customTagSection: 1,
                label: i18n.t('customTag.edit.name'),
                type: 'input',
                path: ['name'],
                isMandatory: true,
            },
            {
                name: 'description',
                customTagSection: 1,
                label: i18n.t('customTag.edit.description'),
                type: 'input',
                path: ['description'],
            },
            {
                name: 'clientId',
                customTagSection: 2,
                label: i18n.t('client'),
                type: 'select',
                path: ['clientId'],
                options: this.clients,
                isMandatory: true,
                promise: (value) => this.getClient(value),
                fieldResets: (model) => {
                    model.divisionId = []
                    model.portfolioId = []
                    model.industry = undefined
                    model.productType = undefined
                    model.respondentType = undefined
                    model.surveyType = undefined
                },
            },
            {
                name: 'oid',
                customTagSection: 3,
                label: i18n.t('customTag.edit.level'),
                type: 'select',
                path: ['oid'],
                options: this.getLevels(),
                isMandatory: true,
                fieldResets: (model) => {
                    model.options = []
                    model.mergefield = undefined
                },
                isHidden: this.isCustomBenchmark,
            },
            {
                name: 'industry',
                customTagSection: 3,
                label: i18n.t('industry'),
                type: 'select',
                path: ['industry'],
                options: this.industries,
                isMandatory: [9, 10].includes(this.customTag.oid),
                isHidden: (!this.isCustomBenchmark && !this.customTag.oid)
                    || this.industries.length === 0
                    || [3, 4, 11].includes(this.customTag.oid),
                placeholder: ![9, 10].includes(this.customTag.oid) ? i18n.t('all') : undefined,
                promise: (value) => this.setProductTypes(value),
                fieldResets: (model) => {
                    model.productType = undefined
                    model.respondentType = undefined
                    model.surveyType = undefined
                },
                isClearable: true,
            },
            {
                name: 'productType',
                customTagSection: 3,
                label: i18n.t('product_type'),
                type: 'select',
                path: ['productType'],
                options: this.productTypes,
                isMandatory: ![6, 7, 9].includes(this.customTag.oid),
                isHidden: (!this.isCustomBenchmark && !this.customTag.oid)
                    || this.customTag.oid <= 5
                    || this.customTag.oid === 11,
                placeholder: (this.isCustomBenchmark || [6, 7, 9].includes(this.customTag.oid))
                    ? i18n.t('all')
                    : undefined,
                promise: (value) => this.setSurveyTypes(value),
                fieldResets: (model) => {
                    model.respondentType = undefined
                    model.surveyType = undefined
                },
                isClearable: true,
            },
            {
                name: 'surveyType',
                customTagSection: 3,
                label: i18n.t('survey_type'),
                type: 'select',
                path: ['surveyType'],
                options: this.surveyTypes,
                isMandatory: ![7, 9].includes(this.customTag.oid),
                isHidden: (!this.isCustomBenchmark && !this.customTag.oid)
                    || this.customTag.oid <= 6
                    || this.customTag.oid === 11,
                placeholder: (this.isCustomBenchmark || [7, 9].includes(this.customTag.oid))
                    ? i18n.t('all')
                    : undefined,
                promise: (value) => this.setRespondentTypes(value),
                fieldResets: (model) => {
                    model.respondentType = undefined
                },
                isClearable: true,
            },
            {
                name: 'respondentType',
                customTagSection: 3,
                label: i18n.t('respondent_type'),
                type: 'select',
                path: ['respondentType'],
                options: this.respondentTypes,
                isMandatory: ![7, 9].includes(this.customTag.oid),
                isHidden: (!this.isCustomBenchmark && !this.customTag.oid)
                    || this.customTag.oid <= 6
                    || this.customTag.oid === 11,
                placeholder: (this.isCustomBenchmark || [7, 9].includes(this.customTag.oid))
                    ? i18n.t('all')
                    : undefined,
                isClearable: true,
            },
             {
                name: 'divisionId',
                customTagSection: 2,
                label: i18n.t('division'),
                type: 'select',
                path: ['divisionId'],
                options: this.divisions,
                isMulti: true,
                isHidden: R.pathOr(9, ['customTag', 'oid'], this) > 7
                    || R.pathOr(0, ['customTag', 'clientId'], this) === 0
                    || [3, 4].includes(this.customTag.oid),
                promise: (value) => this.setPortfolios(value),
                fieldResets: (model, value) => {
                    const portfolioIdSet = new Set(model.portfolioId)
                    model.portfolioId = this.clientPortfolios
                        .filter(({ value: portfolioId, divisionId }) => (
                            portfolioIdSet.has(portfolioId) && value.includes(divisionId)
                        ))
                        .map(({ value }) => value)
                },
                placeholder: i18n.t('all'),
            },
            {
                name: 'portfolioId',
                customTagSection: 2,
                label: i18n.t('portfolio'),
                type: 'select',
                path: ['portfolioId'],
                options: this.portfolios,
                isMulti: true,
                isHidden: R.pathOr(0, ['customTag', 'clientId'], this) === 0
                    || R.pathOr(9, ['customTag', 'oid'], this) > 7
                    || [3, 4, 5].includes(this.customTag.oid),
                placeholder: i18n.t('all'),
            },
            {
                name: 'questionType',
                customTagSection: 4,
                label: i18n.t('customTag.edit.questionType'),
                type: 'select',
                path: ['uiQuestionType'],
                options: this.getQuestionTypes(),
                isMandatory: true,
                isHidden: this.isCustomBenchmark,
                isDisabled: R.isNil(this.customTag.oid),
                fieldResets: (model, value) => {
                    model.questionType = value === 'CH-AT' ? 'CH' : value
                    model.options = value === 'CH-AT'
                        ? R.union(model.options, ['adminTag'])
                        : model.options.filter((option) => option !== 'adminTag')
                    model.config.answerOptions = value === 'CH-AT' // auto-add 1 options for CH-AT
                        ? [{ uuid: uuidv4(), config: { langs: { en_GB: R.or(model.name, '') } } }]
                        : []
                    model.config.mergefield = undefined
                    model.config.dateOptions = []
                },
            },
            {
                name: 'options',
                customTagSection: 4,
                label: i18n.t('customTag.edit.options'),
                type: 'checkboxGroup',
                path: ['options'],
                options: this.getOptions(),
                isHidden: (
                    R.isNil(this.customTag.oid)
                        || R.isNil(this.customTag.questionType)
                        || this.customTag.options.includes('adminTag')
                ),
                fieldResets: (model, options) => {
                    if (!options.includes('mergefield')) {
                        model.config.mergefield = undefined
                    }
                    if (!options.includes('benchamrk')) {
                        model.benchmarks = undefined
                    }
                },
            },
            {
                name: 'isMandatory',
                customTagSection: 4,
                label: 'Mandatory',
                type: 'checkbox',
                path: ['config', 'isMandatory'],
                isHidden: (
                    R.isNil(this.customTag.oid)
                        || R.isNil(this.customTag.questionType)
                        || this.customTag.questionType === 'DT'
                        || this.customTag.options.includes('adminTag')
                ),
            },
            {
                // this ony gets applied at the time of entity creation. It doesn't overwrite an unset recordTag value.
                name: 'defaultValue',
                customTagSection: 4,
                label: 'Default value',
                type: 'switch',
                path: ['defaultValue'],
                isHidden: !this.customTag.options.includes('adminTag'),
            },
            {
                name: 'mergefield',
                customTagSection: 4,
                type: 'mergefield',
                path: ['customTag', 'mergefield'],
                isHidden: !this.customTag.options.includes('mergefield'),
            },
            {
                name: 'answerOptions',
                customTagSection: 4,
                type: 'answerOptions',
                path: ['customTag', 'config', 'answerOptions'],
                isHidden: !(
                    ['SB', 'CH'].includes(this.customTag.questionType)
                ),
            },
            {
                name: 'date',
                customTagSection: 4,
                type: 'date',
                path: ['customTag', 'date'],
                isHidden: this.customTag.questionType !== 'DT',
            },
            {
                name: 'benchmarks',
                customTagSection: 4,
                type: 'benchmarks',
                path: ['customTag', 'benchmarks'],
                isHidden: R.isNil(customTag.respondentType) || !(this.customTag.options.includes('benchmark') || this.customTag.oid === 9),
            },
        ]

        return fields.filter(({ isHidden }) => !isHidden)
    }

    @action
    resetState = () => {
        this.productMap = {}
        this.industries = []
        this.productTypes = []
        this.respondentTypes = []
        this.surveyTypes = []
        this.divisions = []
        this.portfolios = []
        this.currentTagGroup = undefined
        this.customTag = {
            name: undefined,
            description: undefined,
            clientId: undefined,
            oid: this.isCustomBenchmark ? 9 : undefined,
            industry: undefined,
            productType: undefined,
            respondentType: undefined,
            surveyType: undefined,
            divisionId: [],
            portfolioId: [],
            questionType: undefined,
            uiQuestionType: undefined,
            options: [],
            defaultValue: false,
            config: {
                mergefield: undefined,
                dateOptions: [],
                answerOptions: [],
                isMandatory: false,
                benchmarkOptions: [],
                benchmarkRules: [],
            },
        }
    }

    @action
    initEdit = async (id) => {
        const { store } = this

        this.resetState()
        if (id === 0) {
            if (this.clients.length === 1) {
                this.customTag.clientId = R.path([0, 'value'], this.clients)
            }
            return
        }

        this.isLoading = true

        const response = await store.http.get(`/intranet/customTag/${id}`)

        this.customTag = R.mergeRight(this.customTag, this.fromJSON(response))

        console.log('initEdit', JSON.stringify(this.customTag, null, 4))

        await this.getClient(this.customTag.clientId)
        this.setPortfolios(this.customTag.divisionId)

        this.currentTagGroup = R.isNil(this.customTag.groupId)
            ? undefined
            : await this.fetchGroup(this.customTag.groupId)

        this.isLoading = false
    }

    initSearch = async () => {
        const { store } = this
        try {
            const response = await store.http.get('/intranet/customTag')
            this.tagList = response
            this.searchData = this.isCustomBenchmark
                ? this.tagList.filter((t) => t.oid === 9)
                : this.tagList
        } catch (e) {
            console.error(e)
            store.addNotitication(null, 'Error getting tags', 'error')
        }
    }

    filter = () => {
        const { searchStr } = this.searchModule
        const re = new RegExp(searchStr, 'i')
        const filteredData = R.isEmpty(searchStr)
            ? this.tagList
            : this.tagList.filter(({
                id,
                name,
                oid,
                clientId,
                industry,
                productType,
                surveyType,
                respondentType,
            }) => (
                (String(id).match(re)
                || name.match(re)
                || String(oid).match(re)
                || String(clientId).match(re)
                || industry?.match(re)
                || productType?.match(re)
                || surveyType?.match(re)
                || respondentType?.match(re))
                && ((this.isCustomBenchmark && oid === 9) || !this.isCustomBenchmark)
            ))

        this.searchData = filteredData
    }

    @action
    getClient = async (clientId) => {
        const { store } = this
        const { http } = store

        const [productMap] = await Promise.all([
            getProductMap(http, clientId, clientId === 0),
            this.getDivisions(clientId),
            this.getPortfolios(clientId),
        ])

        this.productMap = productMap
        this.industries = Object.values(this.productMap).map(({ label, value }) => ({
            label,
            value,
        }))

        // populate options on client select (rather than only once each ancestor field is touched)
        this.setProductTypes(this.customTag.industry)
        this.setSurveyTypes(this.customTag.productType)
        this.setRespondentTypes(this.customTag.surveyType)
    }

    setPortfolios = (divisionId) => {
        this.portfolios = this.clientPortfolios.filter((portfolio) => (
            divisionId.length === 0 || divisionId.includes(portfolio.divisionId)
        ))
    }

    // options at a level always reflect only the ancestors actually chosen (not just immediate ancestor)
    setProductTypes = (industry) => {
        const productTypeNodes = isBlank(industry)
            ? getProductTypes(this.productMap, true)
            : Object.values(R.pathOr({}, [industry, 'children'], this.productMap))
        const mappedProductTypes = productTypeNodes.map(({ label, value }) => ({ label, value }))

        this.productTypes = this.isCustomBenchmark
            ? [{ value: '', label: i18n.t('all') }].concat(mappedProductTypes)
            : mappedProductTypes
    }

    setSurveyTypes = (productType) => {
        const { industry } = this.customTag
        let surveyTypeNodes

        if (isBlank(industry)) {
            surveyTypeNodes = getSurveyTypes(this.productMap, true)
        } else if (isBlank(productType)) {
            const productTypeSubtree = R.pathOr({}, [industry, 'children'], this.productMap)
            surveyTypeNodes = getProductTypes(productTypeSubtree, true)
        } else {
            const surveyTypeSubtree = R.pathOr({}, [industry, 'children', productType, 'children'], this.productMap)
            surveyTypeNodes = Object.values(surveyTypeSubtree)
        }
        const mappedSurveyTypes = surveyTypeNodes.map(({ label, value }) => ({ label, value }))

        this.surveyTypes = this.isCustomBenchmark
            ? [{ value: '', label: i18n.t('all') }].concat(mappedSurveyTypes)
            : mappedSurveyTypes
    }

    setRespondentTypes = (surveyType) => {
        const { industry, productType } = this.customTag
        let respondentTypeNodes

        if (isBlank(industry)) {
            respondentTypeNodes = getRespondentTypes(this.productMap, true)
        } else if (isBlank(productType)) {
            const productTypeSubtree = R.pathOr({}, [industry, 'children'], this.productMap)
            respondentTypeNodes = getSurveyTypes(productTypeSubtree, true)
        } else if (isBlank(surveyType)) {
            const surveyTypeSubtree = R.pathOr({}, [industry, 'children', productType, 'children'], this.productMap)
            respondentTypeNodes = getProductTypes(surveyTypeSubtree, true)
        } else {
            const path = [industry, 'children', productType, 'children', surveyType, 'children']
            respondentTypeNodes = Object.values(R.pathOr({}, path, this.productMap))
        }
        const mappedRespondentTypes = respondentTypeNodes.map(({ label, value }) => ({ label, value }))

        this.respondentTypes = this.isCustomBenchmark
            ? [{ value: '', label: i18n.t('all') }].concat(mappedRespondentTypes)
            : mappedRespondentTypes
    }

    getDivisions = async (clientId) => {
        let divisions = []

        try {
            if (clientId !== 0) {
                divisions = await this.store.http.get(`/division/client/${clientId}`)
            }
        } catch (e) {
            console.error(e)
        }

        this.divisions = divisions.map(({ divisionId, name }) => ({
            value: divisionId,
            label: name,
        }))
    }

    getPortfolios = async (clientId) => {
        let portolios = []

        try {
            if (clientId !== 0) {
                portolios = await this.store.http.get(`/portfolio/client/${clientId}`)
            }
        } catch (e) {
            console.error(e)
        }

        this.clientPortfolios = portolios.map(({ divisionId, portfolioId, name }) => ({
            value: portfolioId,
            label: name,
            divisionId,
        }))

        this.portfolios = this.clientPortfolios
    }

    getClients = async () => {
        const { userData } = this.store
        const { clientId, clientName } = userData
        let clients = []
        try {
            const response = await this.store.http.get('/client')
            if (response.clients.length === 0) {
                clients = [{
                    clientId,
                    name: clientName,
                }]
            } else {
                clients = [{
                    clientId: 0,
                    name: i18n.t('customTag.edit.allClients'),
                }].concat(response.clients)
            }
        } catch (e) {
            clients = [{
                clientId,
                name: clientName,
            }]
        }

        this.clients = clients.map(({ clientId, name }) => ({
            value: clientId,
            label: `${name} [${clientId}]`,
        }))
    }

    @action
    init = async () => {
        await Promise.all([
            this.getClients(),
        ])
        if (this.isCustomBenchmark) {
            this.customTag.oid = 9
        }

        this.isLoading = false
    }

    cancel = (history) => {
        const url = this.isCustomBenchmark
            ? '/intranet/customBenchmark'
            : '/intranet/customTag'

        history.push(url)
    }

    isValid = () => (
        Object.entries(this.formFields)
            .map(([name, validate]) => (
                validate({
                    name,
                    value: this.customTag[name],
                }, true)
            ))
            .every((isValid) => isValid)
    )

    fromJSON = (json) => {
        const customTag = cloneDeep(json)

        customTag.divisionId = R.pathOr([], ['divisionId'], customTag).split(',').filter((v) => !R.isEmpty(v)).map((v) => parseInt(v, 10))
        customTag.portfolioId = R.pathOr([], ['portfolioId'], customTag).split(',').filter((v) => !R.isEmpty(v)).map((v) => parseInt(v, 10))
        customTag.options = R.pathOr([], ['options'], customTag).split(',')

        const dateOptions = R.pathOr([], ['config', 'dateOptions'], customTag)
            .map((option) => {
                option.config.startDate = R.isNil(option.config.startDate)
                    ? undefined
                    : moment(option.config.startDate, 'DD/MM')

                option.config.endDate = R.isNil(option.config.endDate)
                    ? undefined
                    : moment(option.config.endDate, 'DD/MM')

                return option
            })

        customTag.config = {
            mergefield: R.pathOr(undefined, ['config', 'mergefield'], customTag),
            answerOptions: R.pathOr([], ['config', 'answerOptions'], customTag),
            benchmarkOptions: R.pathOr([], ['config', 'benchmarkOptions'], customTag),
            benchmarkRules: R.pathOr([], ['config', 'benchmarkRules'], customTag),
            dateOptions,
            isMandatory: customTag.config.isMandatory,
        }

        customTag.uiQuestionType = customTag.options.includes('adminTag') ? 'CH-AT' : customTag.questionType
        customTag.defaultValue = Boolean(R.pathOr(false, ['defaultValue'], customTag))

        return customTag
    }

    toJSON = () => {
        const { customTag } = this

        return {
            id: customTag.id,
            name: customTag.name,
            description: customTag.description,
            clientId: customTag.clientId,
            oid: customTag.oid,
            industry: customTag.industry || '',
            productType: customTag.productType || '',
            respondentType: customTag.respondentType || '',
            surveyType: customTag.surveyType || '',
            divisionId: customTag.divisionId.slice().join(','),
            portfolioId: customTag.portfolioId.slice().join(','),
            questionType: customTag.questionType,
            options: customTag.options.slice().join(','),
            defaultValue: customTag.defaultValue,
            config: {
                isMandatory: customTag.config.isMandatory,
                mergefield: customTag.config.mergefield,
                answerOptions: customTag.config.answerOptions.slice(),
                dateOptions: R.pathOr([], ['config', 'dateOptions'], customTag)
                    .slice()
                    .map(({
                        uuid, config,
                    }) => ({
                        uuid,
                        config: {
                            langs: config.langs,
                            startDate: !R.isNil(config.startDate) ? config.startDate.format('DD/MM') : undefined,
                            endDate: !R.isNil(config.endDate) ? config.endDate.format('DD/MM') : undefined,
                            repeat: config.repeat,
                        },
                    })),
                benchmarkOptions: R.pathOr([], ['config', 'benchmarkOptions'], customTag).slice(),
                benchmarkRules: R.pathOr([], ['config', 'benchmarkRules'], customTag).slice().map((bo) => ({
                    ...bo,
                    config: {
                        ...bo.config,
                        rules: R.pathOr([], ['config', 'rules'], bo).slice()
                            .map((rule) => ({ ...rule, values: R.pathOr([], ['values'], rule).slice() })),
                    },
                })),
            },
        }
    }

    save = async (history) => {
        if (this.isValid()) {
            const customTag = this.toJSON()

            const url = R.isNil(customTag.id)
                ? '/intranet/customTag'
                : `/intranet/customTag/${customTag.id}`

            const method = R.isNil(customTag.id)
                ? 'post'
                : 'put'

            try {
                const msg = R.isNil(customTag.id)
                    ? 'msg_Item_Successfully_Created'
                    : 'msg_Item_Successfully_Updated'
                await this.store.http[method](url, JSON.stringify(customTag))
                this.store.addNotification({ body: i18n.t(msg), level: 'success' })
                history.push('/intranet/customTag')
            } catch (e) {
                console.error(e)
                const msg = R.isNil(customTag.id)
                    ? 'msg_Item_Failed_Create'
                    : 'msg_Item_Failed_Update'
                this.store.handleError({ body: i18n.t(msg), level: 'error' })
            }
        }
    }

    delete = async (id) => {
        const url = `/intranet/customTag/${id}`
        try {
            await this.store.http.delete(url)
            this.store.addNotification({ body: i18n.t('msg_Item_Successfully_Deleted'), level: 'success' })
            this.initSearch()
            window.scrollTo(0, 0)
        } catch (e) {
            console.error(e)
            this.store.handleError({ body: i18n.t('msg_Item_has_not_been_deleted'), level: 'error' })
        }
    }

    toggleShowBenchmarksModal = () => {
        this.showBenchmarksModal = !this.showBenchmarksModal
    }

    setBenchamrkOptionIndex = (index) => {
        this.benchamrkOptionIndex = index
    }

    toggleTestBenchmark = (tagOptionId) => {
        this.testBenchmark = tagOptionId
    }

    /** customTag groups <START> */
    fetchGroups = async () => {
        try {
            const response = await this.store.http.get('/intranet/customTag/group')
            this.groups = response.length ? response : []
        } catch (e) {
            console.error(e)
            this.store.handleError({ body: 'Failed to load custom tag groups', level: 'error' })
        }
    }

    fetchGroup = async (id) => (
        this.store.http.get(`/intranet/customTag/group/${id}`)
    )

    openNewGroupModal = () => {
        this.groupModalOpen = true
        this.groupForm = {
            editingGroupId: undefined, name: '', cascade: false, tagIdsByOid: {},
        }
    }

    openEditGroupModal = async (group) => {
        const detail = await this.fetchGroup(group.id)
        this.groupForm = {
            editingGroupId: detail.id,
            name: detail.name,
            cascade: Boolean(detail.cascade),
            tagIdsByOid: detail.tags.reduce((acc, tag) => ({ ...acc, [tag.oid]: tag.id }), {}),
        }
        this.groupModalOpen = true
    }

    closeGroupModal = () => {
        this.groupModalOpen = false
    }

    setGroupFormName = (name) => {
        this.groupForm.name = name
    }

    toggleGroupFormCascade = () => {
        this.groupForm.cascade = !this.groupForm.cascade
    }

    setGroupFormTag = (oid, tagId) => {
        this.groupForm.tagIdsByOid = { ...this.groupForm.tagIdsByOid, [oid]: tagId }
    }

    registerGroupField = (name, validate) => {
        this.groupFormFields[name] = validate
    }

    unregisterGroupField = (name) => {
        Reflect.deleteProperty(this.groupFormFields, name)
    }

    isGroupFormValid = () => (
        Object.entries(this.groupFormFields)
            .map(([name, validate]) => (
                validate({ name, value: this.groupForm[name] }, true)
            ))
            .every((isValid) => isValid)
    )

    saveGroupForm = async () => {
        if (!this.isGroupFormValid()) return

        const {
            name, cascade, tagIdsByOid, editingGroupId,
        } = this.groupForm

        const tagIds = Object.values(tagIdsByOid)
            .filter((tagId) => !R.isNil(tagId))
            .map(String)

        const url = R.isNil(editingGroupId)
            ? '/intranet/customTag/group'
            : `/intranet/customTag/group/${editingGroupId}`
        const method = R.isNil(editingGroupId) ? 'post' : 'put'

        try {
            await this.store.http[method](url, JSON.stringify({ name, cascade, tagIds }))
            this.store.addNotification({
                body: R.isNil(editingGroupId) ? 'Custom tag group created' : 'Custom tag group updated',
                level: 'success',
            })
            this.closeGroupModal()
            this.initSearch()
            this.fetchGroups()
        } catch (e) {
            console.error(e)
            this.store.handleError({ body: 'Failed to save custom tag group', level: 'error' })
        }
    }

    deleteGroup = async (id) => {
        const url = `/intranet/customTag/group/${id}`

        try {
            await this.store.http.delete(url)
            this.store.addNotification({ body: 'Custom tag group deleted', level: 'success' })
            this.initSearch()
            this.fetchGroups()
        } catch (e) {
            console.error(e)
            this.store.handleError({ body: 'Failed to delete custom tag group', level: 'error' })
        }
    }

    getAvailableAdminTagOptions = (oid) => {
        const { editingGroupId } = this.groupForm

        return this.tagList
            .filter((tag) => (
                tag.oid === oid
                && (tag.groupId === null || tag.groupId === editingGroupId)
                && (tag.options || '').split(',').includes('adminTag')
            ))
            .map((tag) => ({ value: tag.id, label: tag.name }))
    }
    /** customTag groups <END> */
}

export default customTagStore
